import React, { useRef, useState } from 'react';

interface FOADocumentsSectionProps {
  initialFaxName: string;
  initialOfferName: string;
  onFaxFileChange: (file: File) => void;
  onOfferFileChange: (file: File) => void;
}

const FOADocumentsSection: React.FC<FOADocumentsSectionProps> = ({
  initialFaxName,
  initialOfferName,
  onFaxFileChange,
  onOfferFileChange,
}) => {
  const [faxFileName, setFaxFileName] = useState<string>(initialFaxName);
  const [offerFileName, setOfferFileName] = useState<string>(initialOfferName);
  const faxInputRef = useRef<HTMLInputElement>(null);
  const offerInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'fax' | 'offer'
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    if (type === 'fax') {
      setFaxFileName(file.name);
      onFaxFileChange(file);
    } else {
      setOfferFileName(file.name);
      onOfferFileChange(file);
    }
  };

  const renderUploadBox = (
    type: 'fax' | 'offer',
    label: string,
    fileName: string,
    inputRef: React.RefObject<HTMLInputElement | null>
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div
        className="border-2 border-gray-300 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 transition-colors"
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg"
          className="hidden"
          onChange={(e) => handleFileChange(e, type)}
        />
        {fileName ? (
          <div className="flex items-center justify-center space-x-2">
            <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm text-gray-700">{fileName}</span>
          </div>
        ) : (
          <div>
            <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="mt-2 text-sm text-gray-600">Click to upload {label.toLowerCase()}</p>
            <p className="text-xs text-gray-500">PDF, PNG, JPG up to 10MB</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="border-t border-gray-200 pt-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Documents</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {renderUploadBox('fax', 'Fax Image', faxFileName, faxInputRef)}
        {renderUploadBox('offer', 'Offer Image', offerFileName, offerInputRef)}
      </div>
    </div>
  );
};

export default FOADocumentsSection;
